import React, { createContext, useContext, useState, useEffect } from "react";
import { authApi } from "../api/auth";
import { isTokenExpired, getTokenTimeRemaining, decodeJwt } from "../utils/security";
import axios from "axios";
import { services } from "../services/api";
import WebSocketManager from "../websocket/WebSocketManager";

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage if available to prevent "role loss" on reload
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem("userProfile");
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error("Failed to parse user profile from storage", e);
      return null;
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [profileCompleted, setProfileCompleted] = useState(true); // Default to true until we know otherwise
  
  // Session warning state
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);
  const [sessionExpiry, setSessionExpiry] = useState(null); // Timestamp of expiry
  const [warningType, setWarningType] = useState('app'); // 'app' or 'broker'
  const [expiringBrokerName, setExpiringBrokerName] = useState(null);
  
  const WARNING_THRESHOLD = 300; // Warn 5 minutes before expiry (in seconds)
  const CHECK_INTERVAL = 60000; // Check every minute

  // Broker Session State (moved from Navbar)
  const [brokerSessions, setBrokerSessions] = useState([]);
  // `Loading` is the FIRST-LOAD flag: true until the first fetch settles, never
  // true again (see fetchBrokerSessions). `Refreshing` is true during EVERY fetch,
  // including that first one. The two are different questions — "do we have
  // anything to show yet?" vs "is something in flight right now?" — and screens
  // need both: the first decides whether to show a placeholder AT ALL, the second
  // drives a quiet in-place indicator. With only the first flag, a refetch was
  // invisible, so a screen had no way to say "updating" without swapping itself
  // out for a spinner.
  const [brokerSessionsLoading, setBrokerSessionsLoading] = useState(true);
  const [brokerSessionsRefreshing, setBrokerSessionsRefreshing] = useState(false);
  const [brokerSessionsError, setBrokerSessionsError] = useState("");
  const [brokerSessionExpiry, setBrokerSessionExpiry] = useState(null);
  // We keep track of the earliest expiring broker session to show in Navbar and Warnings



  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("accessToken");
      
      // Optimistic check: if we have a non-expired token, consider logged in immediately
      // This prevents the "login flash" or asking to login again on new tabs
      if (token && !isTokenExpired(token)) {
        setIsAuthenticated(true);
        const decoded = decodeJwt(token);
        if (decoded?.exp) {
          setSessionExpiry(decoded.exp * 1000); // Convert to ms
        }
        
        // If we already have a user from localStorage (initialized in useState), 
        // we are good to go, but we should still fetch fresh data in background.
        if (user) {
           setProfileCompleted(user.profile_completed || false);
        }
      } else {
        // Token invalid or missing
        if (user) setUser(null); // Clear optimistic user if token is bad
        localStorage.removeItem("userProfile");
      }

      if (token) {
        try {
          // Check if token is actually expired or close to it
          if (isTokenExpired(token)) {
            throw new Error("Token expired locally");
          }

          // Try to get current user with existing token
          const userData = await authApi.getCurrentUser();
          
          // Update state and cache
          setUser(userData);
          localStorage.setItem("userProfile", JSON.stringify(userData));
          
          setIsAuthenticated(true);

          // Check if profile is completed
          setProfileCompleted(userData.profile_completed || false);
        } catch (error) {
          // Only attempt refresh/logout if it's an authentication error (401)
          // or if the token is locally expired.
          // Ignore network errors (failed to fetch) to prevent logging out when backend is unreachable.
          if (
             error.message === "Token expired locally" || // Our manual check
             (error.response && error.response.status === 401) // Backend rejected it
          ) {
              try {
                console.log(
                  "Token expired or rejected. Attempting to refresh token..."
                );
                await authApi.refreshToken();
                // Get new token expiry
                const newToken = localStorage.getItem("accessToken");
                if (newToken) {
                    const decoded = decodeJwt(newToken);
                    if (decoded?.exp) {
                        setSessionExpiry(decoded.exp * 1000);
                    }
                }

                const userData = await authApi.getCurrentUser();
                
                // Update state and cache
                setUser(userData);
                localStorage.setItem("userProfile", JSON.stringify(userData));
                
                setIsAuthenticated(true);
                setProfileCompleted(userData.profile_completed || false);
              } catch (refreshError) {
                console.error("Token refresh failed:", refreshError);
                // Only clear if refresh failed with an AUTH error, not network error
                if (refreshError.response && refreshError.response.status === 401) {
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("userProfile");
                    setIsAuthenticated(false);
                    setUser(null);
                    setProfileCompleted(true);
                }
              }
          } else {
              // For network errors or other non-auth errors, we DO NOT clear the session.
              // We might want to set a "offline" or "connection error" state here.
              console.error("Auth check failed due to non-auth error (likely network):", error);
              // For now, we can leave isAuthenticated as false (default) but keep the token?
              // Or better, if we have a valid token locally, maybe we treat them as "Offline Logged In"?
              // For safety, let's just NOT remove the token.
              
              // IF we have a cached user, we stay authenticated!
              if (token && !isTokenExpired(token)) {
                  setIsAuthenticated(true);
              }
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Broker Session Logic
  const recalculateEarliestExpiry = (sessions) => {
    // Filter for active sessions only
    const activeSessions = sessions.filter(s => 
        (s.session_status?.toLowerCase() === 'active' || s.session_status?.toLowerCase() === 'connected') && 
        s.session_expires_at
    );

    if (!activeSessions || activeSessions.length === 0) {
        setBrokerSessionExpiry(null);
        setExpiringBrokerName(null);
        return;
    }

    // Find min expiry
    // Sort by expiry to find the earliest
    activeSessions.sort((a, b) => new Date(a.session_expires_at).getTime() - new Date(b.session_expires_at).getTime());
    
    const earliest = activeSessions[0];
    const minExpiry = new Date(earliest.session_expires_at).getTime();
    
    setBrokerSessionExpiry(minExpiry);
    setExpiringBrokerName(earliest.broker_name || earliest.brokerId || earliest.account_id);
  };

  // `brokerSessions` is the app's live broker-account state: seeded by this fetch,
  // then kept current by the broker-session.{userId} WS subscription below. Screens
  // should READ it rather than re-fetching /broker-accounts themselves — see
  // TradeAccounts.jsx, which used to do its own duplicate fetch + a Refresh button.
  // loading/error are tracked so those screens don't need their own copy of either.
  const fetchBrokerSessions = async () => {
    // Guard OUTSIDE the try: inside it, this early return still runs `finally`,
    // which would clear the loading flag without ever having fetched.
    if (!isAuthenticated) return;

    setBrokerSessionsRefreshing(true);
    try {
        setBrokerSessionsError("");
        const response = await services.brokerAccounts.getAccounts();
        const sessions = response.data || [];
        setBrokerSessions(sessions);
        recalculateEarliestExpiry(sessions);
    } catch (error) {
        console.error("Failed to fetch broker sessions", error);
        setBrokerSessionsError(error?.message || "Failed to load broker accounts");
    } finally {
        // First load only — a pull-to-refresh must not re-trigger a full-page spinner.
        setBrokerSessionsLoading(false);
        setBrokerSessionsRefreshing(false);
    }
  };

  // Initial fetch and WebSocket subscription for Broker Sessions
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    // Initial fetch
    fetchBrokerSessions();

    // WebSocket subscription for real-time updates
    const channel = `broker-session.${user.id}`;
    
    // Handler for session events
    const handleSessionEvent = (data) => {
         console.log("🔔 WebSocket update for broker session (AuthContext):", data);
         
         setBrokerSessions(prev => {
             // Create a deep copy to ensure state update triggers
             let newSessions = [...prev];
             
             // The event payload might have account_id in separate field or inside data
             // Backend payload: { type, account_id, expires_at, status, ... }
             const targetAccountId = data.account_id;
             
             if (!targetAccountId) return prev;

             const index = newSessions.findIndex(s => s.id === targetAccountId || s.account_id === targetAccountId);
             
             if (data.type === 'session_deleted' || data.status === 'expired') {
                 if (index !== -1) {
                     newSessions[index] = { 
                        ...newSessions[index], 
                        session_status: 'expired', 
                        session_expires_at: null 
                     };
                 }
             } else {
                 // Session Created or Updated
                 const updatedFields = {
                    session_expires_at: data.expires_at,
                    session_status: data.session_status || 'active'
                 };

                 if (index !== -1) {
                     // Update existing
                     newSessions[index] = { ...newSessions[index], ...updatedFields };
                 } else {
                     // Trigger refetch for new sessions to get full details
                     fetchBrokerSessions();
                     return prev; 
                 }
             }
             
             // Recalculate expiry with new data
             recalculateEarliestExpiry(newSessions);
             return newSessions;
         });
    };

    // Subscribe
    const unsubscribe = WebSocketManager.subscribeTopic(channel, handleSessionEvent);

    return () => {
        if (unsubscribe && typeof unsubscribe === 'function') unsubscribe();
    };
  }, [isAuthenticated, user?.id]);


  // Session monitor effect with cross-tab sync
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSession = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        //! We are still flagged authenticated but the access token is GONE — the
        //! only way that happens mid-session is a rejected refresh, which deletes
        //! it. The old `return` here left this monitor permanently BLIND: it bailed
        //! on every subsequent tick, so the `remaining === 0 -> logout()` branch
        //! below became unreachable and the app never logged out or redirected.
        //! Combined with an un-dismissible warning modal whose countdown is purely
        //! local state, that stranded the user on a dead dialog counting down to
        //! nothing, with a "Stay Logged In" button that could never succeed.
        console.warn("[auth] session monitor: access token is gone — ending session");
        setShowSessionWarning(false);
        await logout();
        return;
      }

      const remaining = getTokenTimeRemaining(token);
      setSessionTimeRemaining(remaining);

      // --- App Session Logic ---
      // If within threshold and not expired
      if (remaining < WARNING_THRESHOLD && remaining > 0) {
         // Try to auto-refresh first involved of showing warning immediately
         // This solves the "30 minute popup" annoyance
         console.log("Details: App session getting close to expiry, attempting silent refresh...");
         const success = await refreshAccessToken();
         if (!success) {
            // Only show warning if refresh failed
            setWarningType('app');
            setShowSessionWarning(true);
         } else {
            // Refresh succeeded
            console.log("Silent refresh successful, session extended.");
            setShowSessionWarning(false);
         }
      } else if (remaining > WARNING_THRESHOLD) {
         // Sync cross-tab dismissal
         if (warningType === 'app') { 
            setShowSessionWarning(false);
         }
      } else if (remaining === 0) {
        // Token expired
        const success = await refreshAccessToken();
        if (!success) {
            logout();
        } else {
            setShowSessionWarning(false);
        }
      }

      // --- Broker Session Logic ---
      if (brokerSessionExpiry) {
        const now = Date.now();
        const brokerRemainingSeconds = (brokerSessionExpiry - now) / 1000;
        
        if (brokerRemainingSeconds < WARNING_THRESHOLD && brokerRemainingSeconds > 0) {
           // Broker session expiring soon!
           // We override the app warning if broker is more urgent or just show it
           setWarningType('broker');
           setShowSessionWarning(true); 
        } else if (brokerRemainingSeconds > WARNING_THRESHOLD) {
           if (warningType === 'broker') {
               setShowSessionWarning(false);
           }
        }
      }
    };


    // Check immediately
    checkSession();

    // Then check periodically - increased frequency for better responsiveness
    const intervalId = setInterval(checkSession, 5000); 

    // Listen for storage events (cross-tab sync of access token)
    const handleStorageChange = (e) => {
      if (e.key === "accessToken") {
        console.log("Access token updated in another tab, syncing session state...");
        checkSession();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isAuthenticated]);

  const stayLoggedIn = async () => {
    const success = await refreshAccessToken();
    if (success) {
      setShowSessionWarning(false);
      return;
    }
    //! The failure branch used to be EMPTY — the button did nothing at all, and
    //! since the modal is deliberately `dismissible={false}` (no X, no ESC, no
    //! backdrop) the only remaining exit was Logout. Clicking the primary action
    //! and having literally nothing happen is the worst possible answer here.
    //! The session cannot be extended if the refresh was rejected, so say so by
    //! doing the honest thing: end it and return to the login screen.
    console.warn("[auth] stayLoggedIn: refresh failed — session cannot be extended, logging out");
    setShowSessionWarning(false);
    await logout();
  };

  const login = async (email, password) => {
    try {
      const response = await authApi.login(email, password);
      // Only store access token in localStorage (refresh token is in HTTP-only cookie)
      setIsAuthenticated(true);
      
      setUser(response.user);
      localStorage.setItem("userProfile", JSON.stringify(response.user));

      //--> Flag a fresh sign-in so App can play the brand "welcome" reveal once
      //    (a silent session-restore does NOT set this, so it won't replay on refresh).
      try {
        sessionStorage.setItem(
          "ds-welcome",
          response.user?.first_name || response.user?.display_name || response.user?.name || ""
        );
      } catch {}

      // Check if profile is completed
      setProfileCompleted(response.user?.profile_completed || false);

      return response;
    } catch (error) {
      console.error("Login error in context:", error);
      throw error; // Simply rethrow the error to preserve the message
    }
  };

  const register = async (email, password) => {
    setLoading(true);
    try {
      const response = await authApi.register(email, password);
      console.log("Auth response:", response);

      if (!response.access_token) {
        throw new Error("Invalid server response");
      }

      // Set authenticated state and user data
      localStorage.setItem("accessToken", response.access_token);
      setIsAuthenticated(true);

      // New users need to complete their profile
      setProfileCompleted(false);

      // Fetch user from server to ensure state is up-to-date
      try {
        const userData = await authApi.getCurrentUser();
        setUser(userData);
        localStorage.setItem("userProfile", JSON.stringify(userData));
      } catch (e) {
        setUser(response.user); // fallback
        localStorage.setItem("userProfile", JSON.stringify(response.user));
      }
      setLoading(false);
      return response;
    } catch (error) {
      console.error("Registration error in context:", error);
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // This will clear the refresh token cookie on the server
      await authApi.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("userProfile");
      setIsAuthenticated(false);
      setUser(null);
      setProfileCompleted(true); // Reset to default
      //! SessionTimeoutMonitor is mounted at the App root and is NOT gated on
      //! isAuthenticated, so a warning left standing here would follow the user
      //! onto the login screen as an un-dismissible modal.
      setShowSessionWarning(false);
    }
  };

  const refreshAccessToken = async () => {
    try {
      console.log("Explicitly refreshing access token...");
      const response = await authApi.refreshToken();

      if (response.access_token) {
        // Also refresh user data after token refresh
        try {
          const userData = await authApi.getCurrentUser();
          setUser(userData);
          localStorage.setItem("userProfile", JSON.stringify(userData));

          // Check if profile is completed
          if (userData && userData.profile_completed !== undefined) {
            setProfileCompleted(userData.profile_completed);
          }

          return true;
        } catch (getUserError) {
          console.error(
            "Error fetching user data after token refresh:",
            getUserError
          );
          return !!response.access_token;
        }
      }

      return !!response.access_token;
    } catch (error) {
      console.error("Token refresh error in context:", error);
      return false;
    }
  };

  const fetchUserData = async () => {
    try {
      console.log("Explicitly fetching fresh user data...");
      const userData = await authApi.getCurrentUser();
      setUser(userData);
      localStorage.setItem("userProfile", JSON.stringify(userData));

      // Check if profile is completed
      if (userData && userData.profile_completed !== undefined) {
        setProfileCompleted(userData.profile_completed);
      }

      return userData;
    } catch (error) {
      console.error("Error fetching user data:", error);

      // Try refreshing token if it's an auth error
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        const refreshSuccessful = await refreshAccessToken();
        if (refreshSuccessful) {
          return await fetchUserData();
        }
      }

      throw error;
    }
  };

  const updateUserProfile = async (profileData) => {
    try {
      console.log("ProfileData being sent to updateProfile:", profileData);

      // Update the profile through the auth service only
      // The auth service will internally update the user-management service
      const authResponse = await authApi.updateProfile(profileData);
      console.log("Response from auth service updateProfile:", authResponse);

      // Get fresh user data from auth service
      try {
        const freshUserData = await authApi.getCurrentUser();
        console.log("Fresh user data after profile update:", freshUserData);

        setUser(freshUserData);
        localStorage.setItem("userProfile", JSON.stringify(freshUserData));

        // Update profile completed status if needed
        if (freshUserData && freshUserData.profile_completed !== undefined) {
          setProfileCompleted(freshUserData.profile_completed);
        }

        return freshUserData;
      } catch (getUserError) {
        console.error(
          "Profile update succeeded but error getting updated user data:",
          getUserError
        );

        // Use the user data from the update response as a fallback
        if (authResponse && authResponse.user) {
          setUser(authResponse.user);
          localStorage.setItem("userProfile", JSON.stringify(authResponse.user));
          return authResponse.user;
        }

        throw new Error("Failed to get updated user data after profile update");
      }
    } catch (error) {
      console.error("Profile update error in context:", error);

      // Try token refresh if it's an auth error
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        try {
          const refreshSuccessful = await refreshAccessToken();
          if (refreshSuccessful) {
            // Retry the update with the new token
            return await updateUserProfile(profileData);
          }
        } catch (refreshError) {
          console.error(
            "Token refresh failed during profile update:",
            refreshError
          );
        }
      }

      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        register,
        refreshAccessToken,
        loading,
        profileCompleted,
        updateUserProfile,
        fetchUserData,
        // Session exports
        showSessionWarning,
        setShowSessionWarning,
        sessionTimeRemaining,
        sessionExpiry,
        stayLoggedIn,
        // Broker Session exports
        brokerSessions,
        brokerSessionsLoading,
        brokerSessionsRefreshing,
        brokerSessionsError,
        brokerSessionExpiry,
        fetchBrokerSessions,
        warningType,
        expiringBrokerName
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
